<template>
  <v-container class="h-100 pa-4" fluid>
    <v-form ref="form" @submit="onSubmit">
      <v-sheet class="bg-background flex-1-1 px-4">
        <v-row>
          <v-col cols="12" md="12">
            <v-text-field
              v-model="note.title"
              label="Title"
              :rules="[rules.required]"></v-text-field>

            Content
            <Editor
              v-model="note.content"
              :init="{
                suffix: '.min',
                plugins: 'lists link image table code',
                toolbar:
                  'undo redo | blocks | bold italic | alignleft aligncenter alignright | bullist numlist | link image table | code',
                skin: 'oxide',
                content_css: 'default',
              }"
              licenseKey="gpl"></Editor>

            <v-checkbox v-model="note.private" label="Private" checked>
              <v-tooltip activator="parent" location="end"
                >Will not show to teams</v-tooltip
              ></v-checkbox
            >
            <v-checkbox v-model="note.hide" label="Hidden">
              <v-tooltip activator="parent" location="end"
                >Defaults to not showing on the notes page</v-tooltip
              ></v-checkbox
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              :loading="isSubmitting"
              color="secondary"
              block
              class="rounded-lg mb-8"
              elevation="2"
              @click="emit('noteEditCanceled')">
              Cancel
            </v-btn>
          </v-col>
          <v-col>
            <v-btn
              :loading="isSubmitting"
              color="primary"
              block
              class="rounded-lg mb-8"
              elevation="2"
              @click="onSubmit">
              {{ saveLabel }}
            </v-btn>
          </v-col>
        </v-row>
      </v-sheet>
    </v-form>
  </v-container>
</template>
<script setup lang="ts">
import Editor from '@tinymce/tinymce-vue';
import { rules } from '@/helpers';
import { computed, ref, toRef, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import { INoteDTO, NoteDTO } from '@/dtos';
import { useNoteStore } from '@/stores/modules/admin';
import { SNACKSTATUS } from '@/stores/util';
import { useUpdateStatusStore } from '@/stores/modules';

interface Props {
  note: INoteDTO;
  isCreate: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(['noteEditComplete', 'noteEditCanceled', 'snack']);
const note = ref<INoteDTO>(new NoteDTO(props.note));
const isCreate = toRef(props, 'isCreate', false);
import '@/components/tinymce/tinymce';
const form = ref<InstanceType<typeof VForm> | null>();
const isSubmitting = ref(false);
const noteStore = useNoteStore();
const statusStore = useUpdateStatusStore();

const saveLabel = computed(() => {
  if (isCreate.value == true) {
    return 'Create Note';
  } else {
    return 'Save';
  }
});

watch(
  () => statusStore.getStatus(`note-${note.value.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `note-${note.value.id}`;
    emit('snack', 'Note', value, statusStore.getError(key));
  },
);

watch(
  () => statusStore.getStatus(`note-create`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `note-create`;
    emit('snack', 'Note', value, statusStore.getError(key));
  },
);

const onSubmit = async () => {
  if (!form.value) {
    return;
  }
  await preformCreate();
  emit('noteEditComplete');
  isSubmitting.value = false;
};

async function preformCreate() {
  isSubmitting.value = true;
  if (isCreate.value) {
    await noteStore.create.mutateAsync(note.value);
  } else {
    await noteStore.update.mutateAsync(note.value);
  }
}
</script>
